import {
    PLACEMENT_BOTTOM,
    PLACEMENT_RIGHT,
    PLACEMENT_TOP,
    TOUR_TYPE__NAVIGATED_TOUR,
} from 'app/shared/help_and_information/tours/TourService';
import { ImsItemHelpDetails } from 'app/shared/ims_items/ImsItemTypeRegistry';
import {
    genericSearchTagsForNewImsItems,
    HelpDetails,
} from 'app/shared/help_and_information/HelpRegistry';
import {
    NAV_TARGET_MANAGE_TAB,
    NAV_TARGET_OVERVIEW,
} from 'app/shared/internal_navigation/nav_targets';
import { ManagePageService } from 'app/overview/manage/service/ManagePageService';
import { IMS_TYPE__ASSET } from 'app/shared/collections/asset';

export const createAssetTour: HelpDetails = {
    key: 'create-asset-tour',
    header: 'Creating an Asset',
    tags: ['Asset'],
    quickStartGuideSearchText: 'using-bulk-edit-mode#adding-an-asset-0',
    hiddenTags: genericSearchTagsForNewImsItems('asset'),
    description: 'This tutorials shows you how to create a new Asset in IMS',
    tourType: TOUR_TYPE__NAVIGATED_TOUR,
    navigateTo: [NAV_TARGET_OVERVIEW, NAV_TARGET_MANAGE_TAB],
    innerTourSteps: [
        {
            placement: 'bottom',
            target: '#overview_tab__manage',
            content: `First, navigate to the Manage Tab of IMS.`,
        },
        {
            placement: 'bottom',
            target: '#manage-all-asset-add-button',
            content: `To create a new Asset, tap the New Asset button.`,
            onShow: () => {
                (
                    (window as any).services.managePageService as ManagePageService
                ).onPlusButtonClicked(IMS_TYPE__ASSET);
            },
            xOffset: -10,
        },
        {
            placement: 'right',
            target: 'asset-body button.dash-confirm-btn',
            content: `
                To save a new Asset, fill out the required details and tap the Create button.
                To learn about editing an Asset, start the 'Editing an Asset' tour.
            `,
            yOffset: -20,
        },
    ],
};

export const assetHelpDetails: ImsItemHelpDetails = {
    header: `Editing an Asset`,
    tags: ['Asset'],
    descriptionHtml: `<p><span class="ims-name">Assets</span> are the physical container for one or more <span class="ims-name">IP devices</span>.
An <span class="ims-name">asset</span> can have location statistics stored against it and as such is visible on the
<span class="ims-name">map</span> (and may move over time).</p>

<p>An <span class="ims-name">asset</span> can be one of an assorted list of <span class="ims-name">asset types</span> and can have
<span class="ims-name">tags</span> associated to it
(for grouping/filtering purposes in <span class="ims-name">dashboard items</span>, <span class="ims-name">alerts</span> etc).</p>

<p>An <span class="ims-name">asset</span> can be <span class="ims-name">integrated</span> against one or more <span class="ims-name">systems</span>
(a fleet management system is an example of a system that may provide location statistics for an <span class="ims-name">asset</span>).</p>`,
    quickStartGuideSearchText: 'using-bulk-edit-mode#adding-an-asset-0',
    tabbedPanelTour: [
        {
            tabKey: 'general',
            title: 'General Tab',
            content: '',
            innerSteps: [
                {
                    key: 'name',
                    title: 'Name',
                    content: `<p>
                        Must be unique. <br/><br/>
                        If this <span class="ims-name">Asset</span> is to receive location statistics from a
                        <span class="ims-name">system</span>, the name must match the name in the
                        <span class="ims-name">system</span> (e.g. DT052 in the IMS will receive
                        locations from DT052 in the fleet management system).
                        </p>
                    `,
                    placement: PLACEMENT_RIGHT,
                },
                {
                    key: 'color',
                    title: 'Colour',
                    content: `<p>
                        Choose an <span class="ims-name">Asset colour</span> from the colour picker.</p>
                        <p>A colour is selected by default.</p>
                    `,
                    placement: PLACEMENT_RIGHT,
                },
                {
                    key: 'asset_template',
                    title: 'Asset Template',
                    content: `<p>
                        Choose from a list of previously created <span class="ims-name">Asset Templates</span>.
                        The selected <span class="ims-name">Asset Templates</span> configuration will be automatically 
                        applied to the <span class="ims-name">Asset</span> to minimise possible configuration 
                        issues if this was done manually.</p>                                                                    
                        <p>When an <span class="ims-name">Asset Templates</span> is applied the following will be 
                        pre-populated using the values as configured on the selected <span class="ims-name">Asset Template</span>:</p>
                        <ul>
                            <li>
                                <span class="ims-name">Type</span>, <span class="ims-name">Tags</span> and 
                                <span class="ims-name">Description</span>
                            </li>
                            <li>
                                <span class="ims-name">Integrations</span> for the <span class="ims-name">Asset</span>
                                will be automatically enabled based those configured on the selected 
                                <span class="ims-name">Asset Template</span>
                            </li>
                            <li>
                                <span class="ims-name">IP Devices</span> that are configured on the 
                                <span class="ims-name">Asset Template</span> will need to be added after creating a new 
                                <span class="ims-name">Asset</span>. 
                            </li>
                        </ul>
                        <p>This dropdown is also used to create a new <span class="ims-name">Asset Template</span>.  
                        Do this by selecting <span class="ims-name">Click to Create</span> after you have correctly 
                        configured, saved and tested an existing <span class="ims-name">Asset</span>.  
                        The result is a new <span class="ims-name">Asset Template</span> will then be available in the 
                        dropdown to apply to other <span class="ims-name">Assets</span>.</p>
                    `,
                    placement: PLACEMENT_RIGHT,
                },
                {
                    key: 'type',
                    title: 'Type',
                    content: `<p>
                        Choose an <span class="ims-name">Asset Type</span> from a predetermined list.</p>
                        <p>The icon will change based on the selection.</p>
                        <p>The size of connection lines drawn on the <span class="ims-name">map</span> will change
                        depending on mobility of the <span class="ims-name">Asset</span>
                        (e.g. vehicles have thin lines, trailers have medium lines and
                        buildings/towers have thick lines).</p>
                    `,
                    placement: PLACEMENT_RIGHT,
                },
                {
                    key: 'is_legacy_anchor',
                    title: 'Legacy Anchor',
                    content: `<p>
                        An <span class="ims-name">Asset</span> may be marked as a legacy anchor in the context of the Entities graph.</p>
                        <p>This means the <span class="ims-name">Asset</span> is used as a "root" to exit the Entities graph.</p>
                        <p>It means features such as <span class="ims-name">Charts</span>, <span class="ims-name">Triggers</span>,
                        the <span class="ims-name">Heat Map</span> and the <span class="ims-name">Map</span> will all behave intuitively
                        by rolling up Entities onto the legacy anchor Asset, Asset Type or Asset Tag (as applicable). 
                    `,
                    placement: PLACEMENT_RIGHT,
                },
                {
                    key: 'tags',
                    title: 'Tags',
                    content: `
                        Allocate one or more <span class="ims-name">tags</span>
                        to the <span class="ims-name">asset</span> for use with:
                        <ul>
                            <li>Configuring <span class="ims-name">dashboard items</span></li>
                            <li>Configuring <span class="ims-name">triggers</span>
                            (for <span class="ims-name">alerts</span>)
                            </li>
                            <li>Filtering within the <span class="ims-name">Asset list</span>
                            in the <span class="ims-name">assets center panel</span></li>
                        </ul>
                    `,
                    placement: PLACEMENT_RIGHT,
                },
                {
                    key: 'description',
                    title: 'Description',
                    content: `<p>Arbitrary information about the
<span class="ims-name">asset</span> (e.g. “Commercial Industries model trailer”)</p>`,
                    placement: PLACEMENT_RIGHT,
                },
            ],
        },
        {
            tabKey: 'devices',
            title: 'IP Devices Tab',
            content: '',
            innerSteps: [
                {
                    key: 'device_list',
                    title: 'Create new IP Device',
                    content: `<p>Pops up a new <span class="ims-name">IP Device</span> panel.</p>`,
                    placement: PLACEMENT_TOP,
                    xOffset: 'center',
                },
                {
                    key: 'device_list',
                    title: 'List of children IP devices',
                    content: `
                        <p>A list of <span class="ims-name">IP devices</span> that are allocated to (contained within)
                        this <span class="ims-name">asset</span>.</p>
                        <p><span class="ims-name">IP devices</span> can be dragged into and out of this section.</p>
                        </p><span class="ims-name">IP devices</span> can be deleted from the IMS by pressing the garbage bin icon.</p>
                        </p><b>WARNING:</b> Deleting an <span class="ims-name">IP device</span> from the IMS will result in the loss of
                        all it's historical data. This operation is not reversible!</p>
                    `,
                    placement: PLACEMENT_BOTTOM,
                    xOffset: 'center',
                },
            ],
        },
        {
            tabKey: 'integrations',
            title: 'Integrations tab',
            content: `
                <p><span class="ims-name">Integrations</span> enable polling to collect data for an <span class="ims-name">asset</span>
                from a <span class="ims-name">system</span>.</p>
                <p>For example, integrating an <span class="ims-name">asset</span> to a fleet management system
                permits location statistics to be pulled from that <span class="ims-name">system</span>
                and matched to the <span class="ims-name">asset</span>.</p>
            `,
            innerSteps: [
                {
                    key: 'integration_key',
                    title: 'Integration Key',
                    content: `
                        <p>The <span class="ims-name">Integration Key</span> can typically be left blank.
                        Its' purpose is to provide additional information to the polling engine for application-specific purposes, e.g.:</p>
                        <ul>
                            <li>If a <span class="ims-name">system</span> uses a non human readable identifier
                            (e.g. a UUID), this key would hold the identifier and the
                            polling engine would know that identifier “xyz” should map to asset DT033</li>
                            <li>If a <span class="ims-name">system</span> needs additional information on a per <span class="ims-name">asset</span>
                            basis (such as an authentication key) it would go here</li>
                        </ul>
                        <br/>
                        (All these examples are application-specific and will change per <span class="ims-name">system</span>).
                    `,
                    placement: PLACEMENT_RIGHT,
                },
            ],
        },
    ],
};
